<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { DateTime } from 'luxon';
import { useViewComposable } from '@/views/composable';
import { getTextVariant } from '@/utils/stringUtils';
import { FLIGHT_RULE_MANAGEMENT, CUSTOMER_QUOTE_FORM, INCENTIVES, CALENDAR_CONFIGURATION, OPTION_CONFIGURATION } from '@/constants';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';

const props = defineProps<{
  listField: any;
  type: string;
  filterQuoteType?: boolean;
  isCloseTab: boolean;
  specialTypes?: boolean;
  groupTypes?: any;
}>();

const emits = defineEmits<{ handleFilterData: [dataFilter: any, dataConfigItemFilter?: any] }>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const { flightRuleTypeList, quoteTypeList, handleCheckQuoteType, multiDataRender, multiDataConfigItemRender } = useViewComposable();
const toast = useToast();
const isExpand = ref(false);
const minDateDefault = new Date(DateTime.utc().minus({ days: 30 }));
const maxDateDefault = new Date();
const viewData = ref<any>({});
const viewDataByType = ref<any>({});
const configurableLabels = [t('common.labels.configurable_item_type'), t('quote_config.flight_type'), t('gqt_admin_setup.gqt_admin_setup_type'), t('common.labels.quote_type')];
const isQuoteConfig = computed(() => configurableLabels.includes(viewData.value?.label));

const initDataFilter = () => {
  return {
    fromDate: minDateDefault,
    toDate: maxDateDefault,
    type: '',
    [multiDataRender(props.type)?.key]: multiDataRender(props.type)?.initFilter,
    quoteType: props.filterQuoteType ? handleCheckQuoteType(props.type) : ''
  };
};

const initConfigType = () => {
  return {
    fromDate: minDateDefault,
    toDate: maxDateDefault,
    type: '',
    [multiDataConfigItemRender(props.type)?.key]: multiDataConfigItemRender(props.type)?.initFilter,
    groupType: props.groupTypes.map((item: any) => item.sk),
    quoteType: props.filterQuoteType ? handleCheckQuoteType(props.type) : '',
    calendarQuotes: multiDataConfigItemRender(props.type)?.initFilter.map((item: any) => item.value)
  };
};

const dataFilter = ref<any>(initDataFilter());
const dataConfigItemFilter = ref<any>(initConfigType());

const handleExpand = (status: boolean) => {
  isExpand.value = !status;
};

const handleApplyFilter = () => {
  for (const key in dataFilter.value) {
    if (!dataFilter.value[key]) {
      delete dataFilter.value[key];
    }
  }
  if (!dataFilter.value.fromDate || !dataFilter.value.toDate) {
    toast.add({ severity: 'error', summary: '', detail: t('create_quote.required_return_date') });
  } else {
    isQuoteConfig.value ? emits('handleFilterData', dataFilter.value, dataConfigItemFilter.value) : emits('handleFilterData', dataFilter.value);
  }
};

const handleResetFilter = () => {
  for (const item of props.listField) {
    dataFilter.value[item.key] = '';
  }
  dataFilter.value.type = '';
  dataFilter.value.quoteType = props.filterQuoteType ? handleCheckQuoteType(props.type) : '';
  dataFilter.value.fromDate = minDateDefault;
  dataFilter.value[multiDataRender(props.type)?.key] = multiDataRender(props.type)?.initFilter;
  dataFilter.value.toDate = maxDateDefault;
  dataConfigItemFilter.value[multiDataConfigItemRender(props.type)?.key] = multiDataConfigItemRender(props.type)?.initFilter;
  dataConfigItemFilter.value.groupType = props.groupTypes.map((item: any) => item.sk);
  dataConfigItemFilter.value.calendarQuotes = multiDataConfigItemRender(props.type)?.initFilter.map((item: any) => item.value);
  for (const key in dataFilter.value) {
    if (!dataFilter.value[key]) {
      delete dataFilter.value[key];
    }
  }
  isQuoteConfig.value ? emits('handleFilterData', dataFilter.value, dataConfigItemFilter.value) : emits('handleFilterData', dataFilter.value);
};

const changeDate = (event: any) => {
  dataFilter.value.fromDate = event[0];
  dataFilter.value.toDate = event[1];
};

const handleRenderOptionQuoteType = () => {
  if (props.type === CUSTOMER_QUOTE_FORM) {
    return quoteTypeList.filter((item: any) => item.value !== INCENTIVES);
  } else {
    return quoteTypeList;
  }
};

const updateModelValue = (event: any, key: string) => {
  dataFilter.value[key] = event.target?.value?.trim();
};

watch(
  () => props.type,
  (newVal) => {
    viewData.value = multiDataRender(newVal);
    viewDataByType.value = multiDataConfigItemRender(newVal);
  },
  { deep: true, immediate: true }
);

watch(
  () => props.isCloseTab,
  (value) => {
    if (value) {
      handleResetFilter();
    }
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <div class="filter">
    <div class="header">
      <div>
        <Button id="btn-close-expand" v-if="isExpand" @click="handleExpand(isExpand)" :label="$t('common.filter')" icon="pi pi-chevron-down" class="p-button-text button-action"></Button>
        <Button id="btn-open-expand" v-if="!isExpand" @click="handleExpand(isExpand)" :label="$t('common.filter')" icon="pi pi-chevron-right" class="p-button-text button-action"></Button>
      </div>
    </div>
    <div v-if="isExpand" class="filter-wrapper">
      <div class="flex gap-3">
        <div v-if="type === FLIGHT_RULE_MANAGEMENT">
          <p class="mb-3 ml-2 font-bold">{{ $t('management.package.type') }}</p>
          <Select id="type-flight" v-model="dataFilter.type" :options="flightRuleTypeList" />
        </div>
        <div v-if="type === CALENDAR_CONFIGURATION" class="flex calendar-filter">
          <div>
            <p class="mb-3 ml-2 font-bold">{{ $t('common.labels.quote_type') }}</p>
            <MultiSelect
              id="quote-type"
              display="chip"
              v-model="dataConfigItemFilter.calendarQuotes"
              :options="viewDataByType?.listOption"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.labels.select_quote_type')"
              class="w-30rem mr-3"
            />
          </div>
          <div>
            <p class="mb-3 ml-2 font-bold">{{ $t('config_items.group_type') }}</p>
            <MultiSelect
              display="chip"
              optionValue="sk"
              id="group-type-selected"
              :optionLabel="getTextVariant(currentLanguage, 'name', 'nameFr')"
              v-model="dataConfigItemFilter.groupType"
              :options="groupTypes"
              :placeholder="$t('advance_quote.form.select_group_type')"
              class="multi-grouptype w-30rem"
            />
          </div>
        </div>
        <div v-else-if="type === OPTION_CONFIGURATION">
          <p class="mb-3 ml-2 font-bold">{{ $t('common.labels.quote_type') }}</p>
          <MultiSelect
            id="quote-type-config"
            display="chip"
            :maxSelectedLabels="4"
            v-model="dataConfigItemFilter[viewDataByType.key]"
            optionLabel="label"
            optionValue="value"
            :options="viewDataByType?.listOption"
            :placeholder="$t('common.labels.select_quote_type')"
            class="w-full mr-3"
          />
        </div>
        <div v-if="filterQuoteType">
          <p class="mb-3 ml-2 font-bold">{{ $t('common.labels.quote_type') }}</p>
          <MultiSelect id="quote-type" display="chip" v-model="dataFilter.quoteType" :options="handleRenderOptionQuoteType()" optionLabel="label" optionValue="value" :placeholder="$t('common.labels.select_quote_type')" class="w-full w-20rem">
            <template #option="slotProps">
              <p>
                {{ slotProps.option.label }}
              </p>
            </template>
          </MultiSelect>
        </div>
        <div v-if="specialTypes && type !== CALENDAR_CONFIGURATION">
          <p class="mb-3 ml-2 font-bold">{{ viewData.label }}</p>
          <MultiSelect
            v-if="isQuoteConfig"
            id="special-quote-types"
            display="chip"
            :maxSelectedLabels="4"
            v-model="dataConfigItemFilter[viewDataByType.key]"
            :options="viewDataByType?.listOption"
            optionLabel="label"
            optionValue="value"
            :placeholder="viewDataByType.placeHolderLabel"
            class="w-full w-20rem"
          >
            <template #option="slotProps">
              <p>
                {{ slotProps.option.label }}
              </p>
            </template>
          </MultiSelect>
          <MultiSelect
            v-else
            id="special-types"
            display="chip"
            :maxSelectedLabels="4"
            v-model="dataFilter[viewData.key]"
            :options="viewData.listOption"
            optionLabel="label"
            optionValue="value"
            :placeholder="viewData.placeHolderLabel"
            class="w-full w-20rem"
          >
            <template #option="slotProps">
              <p>
                {{ slotProps.option.label }}
              </p>
            </template>
          </MultiSelect>
        </div>
        <div class="filter-text flex gap-3">
          <div class="filter-item" v-for="(field, index) in listField" :key="index">
            <p class="mb-3 ml-2 font-bold">{{ field.label }}</p>
            <InputText class="input-field" :modelValue="dataFilter[field.key]" @change="updateModelValue($event, field.key)" />
          </div>
        </div>
        <div class="filter-date">
          <div class="filter-item">
            <p class="mb-3 ml-2 font-bold">{{ $t('common.labels.date_range') }}</p>
            <DatePicker @update:modelValue="changeDate($event)" :modelValue="[dataFilter.fromDate, dataFilter.toDate]" selectionMode="range" dateFormat="dd-M-yy" :selectOtherMonths="true" :placeholder="$t('common.date_format_range')"> </DatePicker>
          </div>
        </div>
      </div>
      <div class="flex gap-3 action-btn">
        <Button id="btn-run-filter" :label="$t('common.labels.run')" class="mt-2 bg-primary" @click="handleApplyFilter()"></Button>
        <Button id="btn-reset-filter" :label="$t('common.labels.reset')" class="mt-2 bg-primary" @click="handleResetFilter()"></Button>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.filter-wrapper {
  .calendar-filter {
    .p-multiselect.p-component {
      .p-multiselect-label-container {
        width: 94% !important;
      }
    }
  }
}
</style>
